import { c as _c } from "react/compiler-runtime";
import type { PropsWithStyles } from '@morpho-org/ui-v6/types';
import type { UrlObject } from 'node:url';
import { Avatar, type AvatarProps } from '@morpho-org/ui-v6/client';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { Button, type ButtonProps } from '@repo/ui';
import { Arrow } from '@repo/ui/icons';
import { cloneElement, type CSSProperties, type ForwardedRef, forwardRef, isValidElement, memo, type ReactNode, useMemo } from 'react';
import { InternalLink } from '@/components/common/InternalLink';
export type modeLink = 'internal' | 'external';
type Url = string | UrlObject;
type IconProps = {
  className?: string;
  style?: CSSProperties;
};

/** Pick the square icon-button size matching a text size's height. */
const toIconSize = (size: ButtonProps['size']): ButtonProps['size'] => {
  switch (size) {
    case 'sm':
    case 'iconSm':
      return 'iconSm';
    case 'md':
    case 'iconMd':
      return 'iconMd';
    case 'lg':
    case 'iconLg':
      return 'iconLg';
    default:
      return 'icon';
  }
};

/** Ergonomic content contract forwarded to the embedded `@repo/ui` Button. */
export interface ButtonLinkButtonProps {
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  label?: ReactNode;
  labelClassName?: string;
  iconLeft?: ReactNode;
  iconLeftProps?: IconProps;
  iconRight?: ReactNode;
  iconRightProps?: IconProps;
  iconAlone?: ReactNode;
  iconAloneProps?: IconProps;
  avatarProps?: AvatarProps;
  /** Raw children; overrides the label/icon assembly when provided. */
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  ['data-testid']?: string;
}
export interface ButtonLinkProps extends PropsWithStyles {
  href: Url;
  mode?: modeLink;
  buttonProps?: ButtonLinkButtonProps;
  prefetch?: boolean;
}
const withProps = (element: ReactNode, props: IconProps | undefined): ReactNode => isValidElement<IconProps>(element) ? cloneElement(element, props ?? {}) : element ?? null;

/**
 * ButtonLink combines a link (internal or external) with a button. Depending on the mode,
 * the link navigates within the app (internal) or opens in a new tab (external). External
 * links get a diagonal arrow affordance on text buttons.
 */
const ButtonLink = (t0, ref) => {
  const $ = _c(48);
  const {
    mode: t1,
    href,
    buttonProps,
    prefetch,
    className
  } = t0;
  const mode = t1 === undefined ? "external" : t1;
  const LinkComponent = mode === "external" ? ExternalLink : InternalLink;
  let t2;
  if ($[0] !== buttonProps) {
    t2 = buttonProps ?? {};
    $[0] = buttonProps;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let avatarProps;
  let buttonClassName;
  let contentOverride;
  let iconAlone;
  let iconAloneProps;
  let iconLeft;
  let iconLeftProps;
  let iconRight;
  let iconRightProps;
  let label;
  let labelClassName;
  let restButtonProps;
  let size;
  let variant;
  if ($[2] !== t2) {
    ({
      variant,
      size,
      label,
      labelClassName,
      iconLeft,
      iconLeftProps,
      iconRight,
      iconRightProps,
      iconAlone,
      iconAloneProps,
      avatarProps,
      children: contentOverride,
      className: buttonClassName,
      ...restButtonProps
    } = t2);
    $[2] = t2;
    $[3] = avatarProps;
    $[4] = buttonClassName;
    $[5] = contentOverride;
    $[6] = iconAlone;
    $[7] = iconAloneProps;
    $[8] = iconLeft;
    $[9] = iconLeftProps;
    $[10] = iconRight;
    $[11] = iconRightProps;
    $[12] = label;
    $[13] = labelClassName;
    $[14] = restButtonProps;
    $[15] = size;
    $[16] = variant;
  } else {
    avatarProps = $[3];
    buttonClassName = $[4];
    contentOverride = $[5];
    iconAlone = $[6];
    iconAloneProps = $[7];
    iconLeft = $[8];
    iconLeftProps = $[9];
    iconRight = $[10];
    iconRightProps = $[11];
    label = $[12];
    labelClassName = $[13];
    restButtonProps = $[14];
    size = $[15];
    variant = $[16];
  }
  const isExternal = mode === "external";
  const isIconOnly = iconAlone != null && label == null && avatarProps == null && contentOverride == null;
  let t3;
  if ($[17] !== iconRight || $[18] !== iconRightProps || $[19] !== isExternal) {
    t3 = isExternal ? <Arrow className={iconRightProps?.className} style={{
      transform: "rotate(-45deg)",
      ...iconRightProps?.style
    }} /> : withProps(iconRight, iconRightProps);
    $[17] = iconRight;
    $[18] = iconRightProps;
    $[19] = isExternal;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  const rightIcon = t3;
  let t4;
  if ($[21] !== avatarProps || $[22] !== contentOverride || $[23] !== iconAlone || $[24] !== iconAloneProps || $[25] !== iconLeft || $[26] !== iconLeftProps || $[27] !== isIconOnly || $[28] !== label || $[29] !== labelClassName || $[30] !== rightIcon) {
    t4 = isIconOnly ? withProps(iconAlone, iconAloneProps) : <>{avatarProps && <Avatar {...avatarProps} />}{withProps(iconLeft, iconLeftProps)}{contentOverride ?? (label != null && (labelClassName ? <span className={labelClassName}>{label}</span> : label))}{rightIcon}</>;
    $[21] = avatarProps;
    $[22] = contentOverride;
    $[23] = iconAlone;
    $[24] = iconAloneProps;
    $[25] = iconLeft;
    $[26] = iconLeftProps;
    $[27] = isIconOnly;
    $[28] = label;
    $[29] = labelClassName;
    $[30] = rightIcon;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  const content = t4;
  let t5;
  if ($[32] !== isIconOnly || $[33] !== size) {
    t5 = isIconOnly ? toIconSize(size) : size;
    $[32] = isIconOnly;
    $[33] = size;
    $[34] = t5;
  } else {
    t5 = $[34];
  }
  let t6;
  if ($[35] !== buttonClassName || $[36] !== content || $[37] !== restButtonProps || $[38] !== t5 || $[39] !== variant) {
    t6 = <Button variant={variant} size={t5} className={buttonClassName} {...restButtonProps}>{content}</Button>;
    $[35] = buttonClassName;
    $[36] = content;
    $[37] = restButtonProps;
    $[38] = t5;
    $[39] = variant;
    $[40] = t6;
  } else {
    t6 = $[40];
  }
  let t7;
  if ($[41] !== LinkComponent || $[42] !== className || $[43] !== href || $[44] !== prefetch || $[45] !== ref || $[46] !== t6) {
    t7 = <LinkComponent href={href} ref={ref} prefetch={prefetch} className={className}>{t6}</LinkComponent>;
    $[41] = LinkComponent;
    $[42] = className;
    $[43] = href;
    $[44] = prefetch;
    $[45] = ref;
    $[46] = t6;
    $[47] = t7;
  } else {
    t7 = $[47];
  }
  return t7;
};
export default memo(forwardRef(ButtonLink));